import React, { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Heading2, Italic, Link2, List, ListOrdered, Minus, Redo2, Undo2 } from "lucide-react";
import "./rich-email-editor.css";

const Tool = ({ on, active, label, testid, children, disabled }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={on}
    disabled={disabled}
    title={label}
    aria-label={label}
    className={`rounded-md p-1.5 transition-colors ${active ? "bg-[color:var(--nq-gold)] text-black" : "text-[color:var(--nq-stone)] hover:text-white"} disabled:opacity-40`}
    data-testid={testid}
  >
    {children}
  </button>
);

/** Word-processor style box for mailouts. Emits HTML plus the plain text version. */
export function RichEmailEditor({ value, onChange, disabled }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true } }),
      Placeholder.configure({ placeholder: "Write your mailout here..." }),
    ],
    content: value || "",
    editable: !disabled,
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML(), ed.getText({ blockSeparator: "\n\n" })),
  });

  // Outside changes (AI format, Undo, opening another draft) replace the content.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "https://";
    const url = window.prompt("Link address (leave empty to remove the link)", prev);
    if (url === null) return;
    if (!url.trim()) editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  const c = () => editor.chain().focus();
  return (
    <div className="nq-editor overflow-hidden rounded-lg border border-[color:var(--nq-line,#2a2f2c)]" data-testid="campaign-body-editor">
      <div className="flex flex-wrap items-center gap-1 border-b border-[color:var(--nq-line,#2a2f2c)] px-2 py-1.5">
        <Tool label="Bold" on={() => c().toggleBold().run()} active={editor.isActive("bold")} disabled={disabled} testid="editor-bold"><Bold className="h-4 w-4" /></Tool>
        <Tool label="Italic" on={() => c().toggleItalic().run()} active={editor.isActive("italic")} disabled={disabled} testid="editor-italic"><Italic className="h-4 w-4" /></Tool>
        <Tool label="Heading" on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} disabled={disabled} testid="editor-heading"><Heading2 className="h-4 w-4" /></Tool>
        <Tool label="Bullet list" on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")} disabled={disabled} testid="editor-bullets"><List className="h-4 w-4" /></Tool>
        <Tool label="Numbered list" on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")} disabled={disabled} testid="editor-numbers"><ListOrdered className="h-4 w-4" /></Tool>
        <Tool label="Link" on={setLink} active={editor.isActive("link")} disabled={disabled} testid="editor-link"><Link2 className="h-4 w-4" /></Tool>
        <Tool label="Divider line" on={() => c().setHorizontalRule().run()} disabled={disabled} testid="editor-divider"><Minus className="h-4 w-4" /></Tool>
        <span className="mx-1 h-4 w-px bg-[color:var(--nq-line,#2a2f2c)]" />
        <Tool label="Undo typing" on={() => c().undo().run()} disabled={disabled || !editor.can().undo()} testid="editor-undo"><Undo2 className="h-4 w-4" /></Tool>
        <Tool label="Redo" on={() => c().redo().run()} disabled={disabled || !editor.can().redo()} testid="editor-redo"><Redo2 className="h-4 w-4" /></Tool>
      </div>
      <EditorContent editor={editor} className="nq-editor-body" data-testid="campaign-body" />
    </div>
  );
}
